import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Panel } from "@/components/KpiCard";
import { ChoixAutre } from "@/components/LogementCard";
import { api, errMsg } from "@/lib/api";

const TYPES_IMMEUBLE = ["Maison de chambres", "Maison", "Bloc", "Entrepôt", "Commercial", "Résidentiel", "Mixte", "Garage", "Parking"];

const ImmeubleLigne = ({ imm, i, n, onSaved, onMove }) => {
  const [nom, setNom] = useState(imm.nom);
  const [adr, setAdr] = useState((imm.adresses || []).join(", "));
  useEffect(() => { setNom(imm.nom); }, [imm.nom]);
  useEffect(() => { setAdr((imm.adresses || []).join(", ")); }, [imm.adresses]);
  const save = async (champs) => {
    try { await api.put(`/immeubles/${imm.id}`, { nom: imm.nom, type: imm.type || "", ...champs }); toast.success("Immeuble enregistré"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); setNom(imm.nom); }
  };
  const renommer = () => { const v = nom.trim(); if (!v || v === imm.nom) { setNom(imm.nom); return; } save({ nom: v }); };
  const saveAdr = () => {
    const l = adr.split(",").map((a) => a.trim()).filter(Boolean);
    if (l.join("|") !== (imm.adresses || []).join("|")) save({ adresses: l });
  };
  const supprimer = async () => {
    if (!window.confirm(`Supprimer l'immeuble « ${imm.nom} » ?`)) { return; }
    try { await api.delete(`/immeubles/${imm.id}`); toast.success("Immeuble supprimé"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid grid-cols-[auto_1fr] md:grid-cols-[auto_1fr_14rem_auto_auto] gap-2 items-center rounded-md border border-slate-200 p-2" data-testid={`immeuble-row-${i}`}>
      <span className="w-5 text-right font-mono text-xs text-slate-400">{i + 1}.</span>
      <Input value={nom} maxLength={120} onChange={(e) => setNom(e.target.value)} onBlur={renommer} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="h-9" data-testid={`immeuble-nom-${i}`} />
      <div className="col-span-2 md:col-span-1"><ChoixAutre value={imm.type} options={TYPES_IMMEUBLE} onCommit={(type) => save({ type })} testId={`immeuble-type-${i}`} /></div>
      <span className="col-start-2 md:col-start-auto text-[11px] text-slate-500 whitespace-nowrap" data-testid={`immeuble-nb-${i}`}>{imm.logements} logement(s)</span>
      <div className="col-start-2 md:col-start-auto flex justify-end">
        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === 0} onClick={() => onMove(i, -1)} title="Monter" data-testid={`ordre-immeuble-monter-${i}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={i === n - 1} onClick={() => onMove(i, 1)} title="Descendre" data-testid={`ordre-immeuble-descendre-${i}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={supprimer} title="Supprimer" data-testid={`immeuble-supprimer-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>
      <Input value={adr} maxLength={600} onChange={(e) => setAdr(e.target.value)} onBlur={saveAdr} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        placeholder="Autres adresses des relevés (ex. 25 St Germain Est)" className="col-span-2 md:col-start-2 md:col-span-4 h-8 text-xs" data-testid={`immeuble-adresses-${i}`} />
    </div>
  );
};

const NouvelImmeuble = ({ onAdd, onCancel }) => {
  const [f, setF] = useState({ nom: "", type: "" });
  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_14rem_auto] gap-2 items-start rounded-md bg-emerald-50/60 p-3" data-testid="immeuble-nouveau">
      <Input value={f.nom} maxLength={120} onChange={(e) => setF({ ...f, nom: e.target.value })} placeholder="Nom de l'immeuble (ex. Gîte Notre-Dame)" className="h-9 bg-white" data-testid="immeuble-nouveau-nom" />
      <ChoixAutre value={f.type} options={TYPES_IMMEUBLE} onCommit={(type) => setF({ ...f, type })} testId="immeuble-nouveau-type" />
      <div className="flex gap-1">
        <Button size="sm" className="h-9 bg-emerald-700 hover:bg-emerald-800" disabled={!f.nom.trim()} onClick={() => onAdd(f)} data-testid="immeuble-nouveau-ajouter"><Plus className="h-3.5 w-3.5 mr-1" />Ajouter</Button>
        <Button size="sm" variant="ghost" className="h-9" onClick={onCancel} data-testid="immeuble-nouveau-annuler">Annuler</Button>
      </div>
    </div>
  );
};

export const ImmeublesPanel = ({ immeubles, onSaved }) => {
  const [ajout, setAjout] = useState(false);
  const ajouter = async (f) => {
    try { await api.post("/immeubles", f); toast.success(`Immeuble « ${f.nom.trim()} » ajouté`); setAjout(false); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const deplacer = async (i, d) => {
    const n = immeubles.map((x) => x.nom);
    [n[i], n[i + d]] = [n[i + d], n[i]];
    try { await api.put("/logements/ordre-immeubles", { immeubles: n }); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Immeubles" testId="immeubles-panel"
      action={<Button size="sm" variant="outline" onClick={() => setAjout(true)} data-testid="add-immeuble-button"><Plus className="h-3.5 w-3.5 mr-1" />Immeuble</Button>}>
      <p className="text-xs text-slate-500 mb-3">Un immeuble peut contenir plusieurs logements (rez-de-chaussée, étages…). L'ordre ci-dessous est utilisé dans les listes, résumés et rapports. Renommer un immeuble met à jour ses logements et les opérations Desjardins rattachées ; un immeuble qui contient des logements ne peut pas être supprimé. Une opération Desjardins sans commentaire ni logement est rattachée automatiquement à l'immeuble dont le nom (St = Saint) ou une adresse apparaît dans sa description.</p>
      <div className="space-y-2">
        {ajout && <NouvelImmeuble onAdd={ajouter} onCancel={() => setAjout(false)} />}
        {immeubles.map((imm, i) => <ImmeubleLigne key={imm.id} imm={imm} i={i} n={immeubles.length} onSaved={onSaved} onMove={deplacer} />)}
        {!immeubles.length && !ajout && <p className="text-sm text-slate-500" data-testid="immeubles-vide">Aucun immeuble pour l'instant.</p>}
      </div>
    </Panel>
  );
};
